<template>
  <div class="user-billing-info">
    <div class="header">
      <h1>User & Billing Information</h1>
      <div class="step-badge">Step 3/3</div>
    </div>

    <validation-observer v-slot="{ validate }" ref="observer">
      <div class="form-container">
        <!-- USER INFO -->
        <h2>User Information</h2>
        <div class="form-row">
          <div class="form-group">
            <label>First Name <span class="required">*</span></label>
            <BaseInput
              v-model="form.userInfo.firstName"
              :rules="'required'"
              name="First Name"
              placeholder="First Name"
            />
          </div>
          <div class="form-group">
            <label>Last Name <span class="required">*</span></label>
            <BaseInput
              v-model="form.userInfo.lastName"
              :rules="'required'"
              name="Last Name"
              placeholder="Last Name"
            />
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label>Email Address <span class="required">*</span></label>
            <BaseInput
              v-model="form.userInfo.email"
              :rules="'required|email'"
              name="Email Address"
              placeholder="Email Address"
              type="email"
              :error="emailError"
              @blur="checkEmail"
            />
          </div>
          <div class="form-group">
            <label>Phone Number <span class="required">*</span></label>
            <BaseInput
              v-model="form.userInfo.phone"
              :rules="'required'"
              name="Phone Number"
              placeholder="Phone Number"
              @input="acceptNumber"
            />
          </div>
        </div>

        <!-- BILLING INFO -->
        <h2>Billing Details</h2>
        <div class="form-row">
          <div class="form-group">
            <label>Address <span class="required">*</span></label>
            <BaseInput
              v-model="form.billingInfo.address"
              :rules="'required'"
              name="Address"
              placeholder="Address"
            />
          </div>
          <div class="form-group">
            <label>City <span class="required">*</span></label>
            <BaseInput
              v-model="form.billingInfo.city"
              :rules="'required'"
              name="City"
              placeholder="City"
            />
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label>State <span class="required">*</span></label>
            <BaseInput
              v-model="form.billingInfo.state"
              :rules="'required'"
              name="State"
              placeholder="State"
            />
          </div>
          <div class="form-group">
            <label>Zip code <span class="required">*</span></label>
            <BaseInput
              v-model="form.billingInfo.zipCode"
              :rules="'required'"
              name="Zip Code"
              placeholder="Zip code"
            />
          </div>
        </div>

        <!-- PAYMENT INFO -->
        <h2>Payment Details</h2>
        <div class="payment-block-wrap border rounded-4 ">
          <div class="payment-header border-bottom d-flex justify-content-between align-items-center p-3 mb-3">
            <div class="left d-flex align-items-center">
              <input type="radio" checked/>
              <h6 class="fw-bold m-0 pl-2">Credit card</h6>
            </div>
            <div class="right">
              <ul class="list-unstyled m-0 p-0 d-flex gap-2">
                <li><img src="/img/price/payment1.svg" alt="Visa"/></li>
                <li><img src="/img/price/payment2.svg" alt="Mastercard"/></li>
                <li><img src="/img/price/payment3.svg" alt="Amex"/></li>
              </ul>
            </div>
          </div>

          <div class="payment-body p-3 ">
            <div class="mb-3">
              <input
                type="text"
                class="form-control rounded-4"
                placeholder="Name on card"
                v-model="form.cardInfo.nameOnCard"
              />
            </div>

            <stripe-element-card
              ref="cardRef"
              :pk="publicKey"
              @token="handleTokenCreated"
            />
            <span class="text-danger">{{ formErrors.stripe }}</span>
            <div class="text-center mt-4">
              <base-button
                type="primary"
                class="primary-orange border-0 mx-auto py-2 rounded-3"
                :disabled="isProcessing"
                @click="() => submitForm(validate)"
              >
                <span v-if="isProcessing">Processing...</span>
                <span  v-else>Pay Now</span>
              </base-button>
            </div>
          </div>
        </div>
      </div>
    </validation-observer>
  </div>
</template>

<script>
import BaseInput from "../Inputs/BaseInput.vue";
import BaseButton from "../BaseButton.vue";
import {ValidationObserver} from "vee-validate";
import {StripeElementCard} from "@vue-stripe/vue-stripe";

export default {
  name: "UserBillingInfo",
  components: {BaseInput, BaseButton, StripeElementCard, ValidationObserver},
  data() {
    return {
      emailVerified: false,
      emailError: '',
      isProcessing: false,
      publicKey:
        process.env.VUE_APP_STRIPE_PUBLIC_KEY ||
        "pk_test_51No6OFL15lkUCZa9hyptqlC7lT0PblOHy62Mu9yb8PfW2oaGrIcjGcbXZtvtLi8tPVtpeE4qCTMxgic6uBX1O60t002oASCfye",
      form: {
        userInfo: {
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
        },
        billingInfo: {
          address: "",
          city: "",
          state: "",
          zipCode: "",
        },
        cardInfo: {
          nameOnCard: "",
          stripe_token: null,
        },
      },
      formErrors: {},
    };
  },
  watch: {
    form: {
      handler() {
        if (this._billingUpdateTimeout) clearTimeout(this._billingUpdateTimeout);
        this._billingUpdateTimeout = setTimeout(() => this.$emit('billing-update'), 800);
      },
      deep: true,
    },
  },
  methods: {
    async checkEmail() {
      if (!this.form.userInfo.email) return;
      try {
        const resp = await this.$http.post("user/valid-email", {
          email: this.form.userInfo.email,
        });

        if (resp.data.status) {
          this.formErrors.email = "";
          this.emailError = "";
          this.emailVerified = true;
        } else {
          this.formErrors.email = resp.data.message;
          this.emailError = resp.data.message;
          this.emailVerified = false;
        }
      } catch (err) {
        this.emailVerified = false;
        this.formErrors.email = "Email validation failed.";
        this.emailError = "Email validation failed.";
      }
    },

    async submitForm() {
      this.formErrors = {};
     let isValid = true;
      if (this.$refs.observer && this.$refs.observer.validate) {
        const res = await this.$refs.observer.validate();
        isValid = res;
      } else {
        isValid = this.isFormValid;
      }
      if (!isValid) return;
      if (!this.emailVerified) {
        this.formErrors.email = "Please verify your email first.";
        return;
      }
      try {
        this.isProcessing = true;
        this.$refs.cardRef.submit();
      } catch (error) {
        this.formErrors.stripe = "Error creating Stripe token.";
        this.isProcessing = false;
      }
    },

    handleTokenCreated(token) {
      this.form.cardInfo.stripe_token = token;
      this.continueSubmit()
    },

    async continueSubmit() {
      if (!this.form.cardInfo?.stripe_token?.id) {
        this.formErrors.stripe = "Failed to generate Stripe token.";
        this.isProcessing = false;
        return;
      }
      this.isProcessing = true;
      this.$emit("form-submitted", {form: {...this.form}});
    },
    acceptNumber() {
      const raw = this.form.userInfo.phone || '';
      const x = raw.replace(/\D/g, '').match(/(\d{0,3})(\d{0,3})(\d{0,4})/);

      if (!x) {
        this.form.userInfo.phone = '';
        return;
      }

      this.form.userInfo.phone =
        !x[2] ? x[1] : `(${x[1]}) ${x[2]}${x[3] ? '-' + x[3] : ''}`;
    }
  },
};
</script>
<style lang="scss" scoped>
* {
  padding: 0;
  margin: 0;
  box-sizing: border-box;
}

.user-billing-info {
  width: 100%;
  margin: 0 auto;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

h1 {
  font-size: 20px;
  line-height: 25px;
  font-weight: 700;
  color: #252525;
  margin: 0;
}

.step-badge {
  position: absolute;
  right: 0;
  top: -7px;
  background: #fff0ea;
  padding: 8px 10px;
  border-radius: 6px;
  font-size: 10px;
  line-height: 23px;
  font-weight: 700;
  color: #252525;
}

.form-container {
  border: 1px solid rgba(235, 26, 38, 0.2);
  padding: 20px 12px;
  border-radius: 15px;
}

h2 {
  font-size: 18px;
  font-weight: 700;
  line-height: 20px;
  color: #252525;
  margin-top: 0;
  margin-bottom: 20px;
}

.form-group {
  flex: 1;
  display: flex;
  flex-direction: column;
}

:deep(.form-control-label) {
  font-size: 12px;
  font-weight: 700;
  line-height: 15px;
  margin-bottom: 12px;
  color: #252525;
}

:deep(.form-control) {
  border: 1px solid rgba(37, 37, 37, 0.1);
  border-radius: 12px;
}

label {
  font-size: 12px;
  font-weight: 700;
  line-height: 15px;
  margin-bottom: 12px;
  color: #252525;
}

.required {
  color: #ff5252;
}

input {
  height: 48px;
  padding: 0 16px;
  border: 1px solid rgba(37, 37, 37, 0.1);
  border-radius: 12px;
  font-size: 16px;
  transition: border-color 0.2s;
}

input:focus {
  outline: none;
  border-color: #4a90e2;
}

input.error {
  border-color: #ff5252;
}

.form-actions {
  margin-top: 30px;
  display: flex;
  justify-content: flex-end;
}

.submit-button {
  background-color: #4a90e2;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.submit-button:hover {
  background-color: #3a7bc8;
}

.payment-block-wrap {
  border: 1px solid rgba(37, 37, 37, 0.1) !important;
  border-radius: 12px !important;
}

.payment-header {
  border-bottom: 1px solid rgba(37, 37, 37, 0.1) !important;
}

.payment-header .left {
  display: flex;
  align-items: center;
}

.payment-header .left input[type="radio"] {
  margin-right: 10px;
  height: 20px;
  width: 20px;
}

.payment-header .left h6 {
  margin-left: 10px;
}

.payment-header .right ul {
  display: flex;
  gap: 10px;
}

.payment-header .right ul li img {
  height: 24px;
}

.primary-orange {
  background-color: #eb1a26 !important;
  color: white;
}

.form-row {
  flex-direction: column;
  gap: 0px;
}

.stripe-card-wrapper iframe {
  border-radius: 1rem !important;
  height: 50px !important;
  border: 1px solid #ccc !important;
  padding: 10px !important;
}

@media (min-width: 768px) {
  .user-billing-info {
    margin-top: 60px;
  }

  h1 {
    font-size: 32px;
    line-height: 40px;
  }

  h2 {
    font-size: 20px;
    font-weight: 700;
    line-height: 25px;
    color: #252525;
    margin-top: 0;
    margin-bottom: 20px;
  }

  .step-badge {
    position: absolute;
    right: 0;
    top: 0;
    background: #fff0ea;
    padding: 10px 14px;
    border-radius: 16px;
    font-size: 18px;
    line-height: 23px;
    font-weight: 700;
    color: #252525;
  }
  .form-container {
    padding: 20px;
  }
  .form-row {
    flex-direction: row;
    gap: 20px;
  }
  label {
    font-size: 15px;
    font-weight: 700;
    line-height: 19px;
    margin-bottom: 12px;
  }
  :deep(.form-control-label) {
    font-size: 15px;
    font-weight: 700;
    line-height: 19px;
    margin-bottom: 12px;
  }
}
</style>
